import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandlerFn,
    HttpRequest,
} from "@angular/common/http";
import { inject } from "@angular/core";
import { Router } from "@angular/router";
import { Observable, catchError, of, tap, throwError } from "rxjs";
import { AuthService } from "./auth.service";
import { CLEAR_TOKENS_ON_RESPONSE, SKIP_AUTH } from "./auth.constants";
import { isJwtTokenExpired } from "./jwt.utils";

export const authInterceptor = (
    req: HttpRequest<unknown>,
    next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
    const authService = inject(AuthService);
    const router = inject(Router);

    // Check if this request should skip authentication (e.g., external API calls)
    const skipAuth = req.context.get(SKIP_AUTH);

    if (skipAuth) {
        // Pass through without adding authentication
        return next(req);
    }

    const sendAuthenticatedRequest = () => {
        // Clone the request object
        let newReq = req.clone();
        if (
            authService.accessToken &&
            !isJwtTokenExpired(authService.accessToken)
        ) {
            newReq = req.clone({
                headers: req.headers.set(
                    "Authorization",
                    "Bearer " + authService.accessToken
                ),
            });
        }

        // Check if this request should clear tokens on response
        const shouldClearTokens = req.context.get(CLEAR_TOKENS_ON_RESPONSE);

        return next(newReq).pipe(
            tap(() => {
                // Clear tokens on successful response if requested
                if (shouldClearTokens) {
                    authService.signOut();
                }
            }),
            catchError((error) => {
                if (
                    error instanceof HttpErrorResponse &&
                    error.status === 401
                ) {
                    authService.signOut();
                    const currentUrl = router.url;
                    router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
                        router.navigateByUrl(currentUrl);
                    });
                    return of();
                }
                else {
                    return throwError(() => error);
                }
            })
        );
    };

    // If the access token is expired or doesn't exist
    if (
        !authService.accessToken ||
        isJwtTokenExpired(authService.accessToken)
    ) {
        authService.signOut();
    }

    return sendAuthenticatedRequest();
};
